import { useRef, useState } from 'react';
import MyReports from './MyReports';
import { useApp } from '../store';
import { useDismiss, useEscapeToClose, useFocusTrap } from '../motion';
import Icon from './Icon';
import { useTabKeys } from './useTabKeys';
import {
  FEEDBACK_KINDS,
  FEEDBACK_PROJECT_NAME,
  feedbackProblem,
  feedbackThanks,
  type FeedbackDraft,
  type FeedbackKindId,
} from '../feedback';
import { BUILD_LABEL, currentEnv } from '../env';

/**
 * Three boxes and a send button.
 *
 * DELIBERATELY NOT THE ISSUE FORM. That form asks for a kind, a priority, an
 * epic, a size and a definition of done — reasonable for somebody who works
 * this board every day and absurd for somebody who has been looking at the
 * software for ten minutes and has found that a button does nothing. Every one
 * of those fields is a decision they are not equipped to make and a reason to
 * close the form instead.
 *
 * The three questions are the ones that make a report actionable, in the order
 * somebody would tell you in person, and none of them is compulsory.
 */
export default function Feedback({ close }: { close: () => void }) {
  const { leaving, dismiss } = useDismiss(close);
  const panelRef = useRef<HTMLDivElement>(null);
  useEscapeToClose(dismiss);
  useFocusTrap(panelRef);
  const sendFeedback = useApp((s) => s.sendFeedback);
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  const view = useApp((s) => s.view);

  /*
   * OPENS ON THE FORM, ALWAYS. Sending is what somebody came here to do far
   * more often than checking, and a tab that guesses — the list when you have
   * reports, the form when you do not — is one that moves under people.
   */
  const FEEDBACK_TABS = ['Tell us what you found', 'Your reports'] as const;
  // R29 — key stays; tab shows the headline-style name.
  const TAB_LABELS: Partial<Record<(typeof FEEDBACK_TABS)[number], string>> = { 'Your reports': 'Your Reports' };
  const [tab, setTab] = useState<(typeof FEEDBACK_TABS)[number]>(
    'Tell us what you found'
  );
  const tabKeys = useTabKeys(FEEDBACK_TABS, tab, setTab);
  const [kindId, setKindId] = useState<FeedbackKindId>('broken');
  const [summary, setSummary] = useState('');
  const [doing, setDoing] = useState('');
  const [expected, setExpected] = useState('');
  const [happened, setHappened] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [sent, setSent] = useState('');
  // Focus goes to the one field that matters, so somebody can start typing.
  const summaryRef = useRef<HTMLInputElement>(null);

  const draft = (): FeedbackDraft => ({ kindId, summary, doing, expected, happened });

  const send = async () => {
    const wrong = feedbackProblem(draft());
    if (wrong) {
      setProblem(wrong);
      summaryRef.current?.focus();
      return;
    }
    setBusy(true);
    setProblem('');
    try {
      const ref = await sendFeedback(draft(), {
        projectName: projects.find((p) => p.id === projectId)?.name ?? projectId,
        view,
        build: BUILD_LABEL,
        env: currentEnv(),
      });
      setSent(ref);
    } catch (e) {
      /*
       * SAID PLAINLY, AND THE WORDS STAY ON SCREEN.
       *
       * The form is not cleared and not closed. Somebody who has just written
       * out three paragraphs and is told "something went wrong" as the box
       * vanishes does not write them again — they stop reporting things, which
       * is the failure that matters rather than the one that was printed.
       */
      const code = (e as { code?: string; message?: string }).code ?? (e as Error).message ?? '';
      setProblem(
        /missing-board/.test(code)
          ? `There is no ${FEEDBACK_PROJECT_NAME} board on this copy to send it to. Nothing has been lost — copy your words out before closing this.`
          : /permission/i.test(code)
            ? `Your account is not allowed to raise issues on the ${FEEDBACK_PROJECT_NAME} board. Ask an administrator, and copy your words out before closing this.`
            : `It could not be sent — ${code || 'no reason given'}. Your words are still here.`
      );
    } finally {
      setBusy(false);
    }
  };

  if (sent)
    return (
      <div className={`overlay setup-overlay ${leaving ? 'is-leaving' : ''}`} role="dialog" aria-modal="true" aria-label="Thank you">
        <div ref={panelRef} tabIndex={-1} className={`feedback ${leaving ? 'is-leaving' : ''}`}>
          <div className="feedback-done">
            <span className="feedback-tick" aria-hidden="true">
              <Icon name="check" size={20} />
            </span>
            <h2>{feedbackThanks(sent)}</h2>
            <p className="hint">
              It is on the backlog with everything else that has been reported. You do not need to
              do anything else with it.
            </p>
            <button className="btn btn-primary" onClick={dismiss}>
              Close
            </button>
          </div>
        </div>
      </div>
    );

  return (
    <div
      className={`overlay setup-overlay ${leaving ? 'is-leaving' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Feedback"
    >
      <div ref={panelRef} tabIndex={-1} className={`feedback ${leaving ? 'is-leaving' : ''}`}>
        <div className="feedback-top">
          <h2>Feedback</h2>
          <button className="modal-close" onClick={dismiss} aria-label="Close">
            <Icon name="close" size={16} />
          </button>
        </div>

        {/*
          ONE ROOM, TWO DOORS WAS THE MISTAKE. Sending a report and finding out
          what happened to it are the same errand, and they were two separate
          buttons in the sidebar — which is fine in something built in an
          evening and wrong in something people pay for. The same tab pattern
          Settings uses, so this reads as part of the product rather than as a
          screen bolted to the side of it.
        */}
        <div className="tabs" role="tablist" onKeyDown={tabKeys.onKeyDown}>
          {FEEDBACK_TABS.map((name) => (
            <button
              key={name}
              role="tab"
              aria-selected={tab === name}
              ref={tabKeys.setRef(name)}
              tabIndex={tabKeys.tabIndex(name)}
              className={`tab ${tab === name ? 'is-on' : ''}`}
              onClick={() => setTab(name)}
            >
              {TAB_LABELS[name] ?? name}
            </button>
          ))}
        </div>

        {tab === 'Your reports' && <MyReports />}

        {tab === 'Tell us what you found' && (
        <>
        <p className="hint feedback-intro">
          It goes straight onto the {FEEDBACK_PROJECT_NAME} board. Only the first line is needed —
          the rest helps, and half an answer is better than none.
        </p>

        <div className="feedback-kinds" role="radiogroup" aria-label="What kind of thing">
          {FEEDBACK_KINDS.map((k) => (
            <button
              key={k.id}
              type="button"
              role="radio"
              aria-checked={kindId === k.id}
              className={`feedback-kind ${kindId === k.id ? 'is-on' : ''}`}
              onClick={() => setKindId(k.id)}
            >
              <span className="feedback-kind-title">{k.label}</span>
              <span className="feedback-kind-blurb">{k.blurb}</span>
            </button>
          ))}
        </div>

        <label className="stack">
          <span className="feedback-q">In one line, what happened? <span className="req">*</span></span>
          <input
            ref={summaryRef}
            className="input"
            autoFocus
            aria-required="true"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Marking a card as done did nothing"
          />
        </label>

        <label className="stack">
          <span className="feedback-q">
            What were you doing?
          </span>
          <textarea
            className="input feedback-box"
            rows={2}
            value={doing}
            onChange={(e) => setDoing(e.target.value)}
            placeholder="I opened a card in the Sandbox and ticked the last check."
          />
        </label>

        <label className="stack">
          <span className="feedback-q">
            What did you expect?
          </span>
          <textarea
            className="input feedback-box"
            rows={2}
            value={expected}
            onChange={(e) => setExpected(e.target.value)}
            placeholder="The card to move to Done."
          />
        </label>

        <label className="stack">
          <span className="feedback-q">
            What happened instead?
          </span>
          <textarea
            className="input feedback-box"
            rows={2}
            value={happened}
            onChange={(e) => setHappened(e.target.value)}
            placeholder="Nothing at all, and the tick came back off when I reopened it."
          />
        </label>

        {problem && (
          <p className="feedback-problem" role="alert">
            {problem}
          </p>
        )}

        </>
        )}

        {tab === 'Tell us what you found' && (
        <div className="feedback-foot">
          {/*
            SAID BEFORE THEY SEND IT rather than discovered afterwards, and the
            list got longer on 12 August: a report now carries the last few
            things they pressed and any errors the page hit — see src/trail.ts —
            because "it did not work" on its own cannot be chased.

            IT MATTERS THAT THIS IS ACCURATE. The report crosses out of their
            company into ours, so this sentence is the only thing standing
            between them and a surprise. What is NOT taken is in trail.ts, and
            the short version is: what was pressed, never what was typed.
          */}
          <span className="hint">
            Sent with the screen you are on, the build you are running, and the last few
            things you pressed. Nothing you typed is included.
          </span>
          <button className="btn" onClick={dismiss} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => void send()} disabled={busy}>
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
        )}

        {/* One way out, wherever they are. The form has Cancel next to Send
            because that pair belongs together; the list has only this. */}
        {tab === 'Your reports' && (
          <div className="feedback-foot">
            <button className="btn btn-primary" onClick={dismiss}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
